import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import useUrlStateObject from "@/hooks/useUrlStateObject";
import { supabase } from "@/lib/supabase";
import { useScheduledSendPoller } from "@/hooks/useScheduledSendPoller";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";
import HistoryFilters, { type FilterState } from "./components/HistoryFilters";
import HistoryTable from "./components/HistoryTable";
import HistoryDetailModal from "./components/HistoryDetailModal";
import { TYPE_CONFIG, rowToHistoryItem } from "./types";
import type { HistoryItem, SendHistoryRow, MsgType, SendStatus } from "./types";

const PAGE_SIZE = 8;

// 발송 이력 필터를 주소(URL)에 남겨 새로고침·링크 공유에도 유지 (sessionStorage 백업)
interface HistoryUrlState extends Record<string, string> {
  q: string;
  from: string;
  to: string;
  types: string;
  statuses: string;
  sort: string;
  dir: string;
  page: string;
}

const HISTORY_URL_DEFAULTS: HistoryUrlState = {
  q: "",
  from: "",
  to: "",
  types: "",
  statuses: "",
  sort: "sentAt",
  dir: "desc",
  page: "1",
};

function paramsToFilter(state: HistoryUrlState): FilterState {
  return {
    search: state.q,
    dateFrom: state.from,
    dateTo: state.to,
    types: state.types ? (state.types.split(",") as MsgType[]) : [],
    statuses: state.statuses ? (state.statuses.split(",") as SendStatus[]) : [],
    sortBy: (state.sort === "count" || state.sort === "cost" ? state.sort : "sentAt") as FilterState["sortBy"],
    sortDir: state.dir === "asc" ? "asc" : "desc",
  };
}

const POLL_INTERVAL_MS = 15000; // 15초마다 자동 폴링

// 탭 복귀(리로드) 시 로딩 없이 마지막 발송 내역을 즉시 보여주기 위한 캐시
const HISTORY_CACHE_KEY = "cache:user_send_history";

function readHistoryCache(): HistoryItem[] | null {
  try {
    const raw = sessionStorage.getItem(HISTORY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HistoryItem[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeHistoryCache(list: HistoryItem[]): void {
  try {
    sessionStorage.setItem(HISTORY_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패는 무시
  }
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const { user, loading, session } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [urlState, setUrlState] = useUrlStateObject<HistoryUrlState>("history_filter", HISTORY_URL_DEFAULTS);
  const filter = useMemo(() => paramsToFilter(urlState), [urlState]);

  const setFilter = useCallback(
    (f: FilterState) => {
      setUrlState({
        ...urlState,
        q: f.search,
        from: f.dateFrom,
        to: f.dateTo,
        types: f.types.join(","),
        statuses: f.statuses.join(","),
        sort: f.sortBy,
        dir: f.sortDir,
        page: "1",
      });
    },
    [urlState, setUrlState]
  );

  const currentPage = Math.max(1, parseInt(urlState.page, 10) || 1);
  const setCurrentPage = useCallback(
    (p: number) => {
      setUrlState({ ...urlState, page: String(Math.max(1, p)) });
    },
    [urlState, setUrlState]
  );

  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(null);
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchDeleting, setBatchDeleting] = useState(false);

  const cachedHistoryRef = useRef<HistoryItem[] | null>(readHistoryCache());
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>(cachedHistoryRef.current ?? []);
  const [dbLoading, setDbLoading] = useState(cachedHistoryRef.current === null);
  const [totalServerCount, setTotalServerCount] = useState(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [pollCountdown, setPollCountdown] = useState(POLL_INTERVAL_MS / 1000);
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const fetchHistory = useCallback(async () => {
    if (!user || !session) return;
    // 캐시된 내역이 이미 있으면 로딩 화면 없이 조용히 갱신
    if (cachedHistoryRef.current === null) setDbLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("get-user-send-history", {
        body: { user_id: user.id, page: 1, pageSize: 500 },
      });

      if (fnError) {
        console.error("[HistoryPage] Edge Function error:", fnError);
        setHistoryItems([]);
        setTotalServerCount(0);
        return;
      }

      if (data?.error) {
        console.error("[HistoryPage] Edge Function error:", data.error);
        setHistoryItems([]);
        setTotalServerCount(0);
      } else if (data?.history && data.history.length > 0) {
        const historyRows = data.history as SendHistoryRow[];

        // Fetch refund transactions for these history items
        const historyIds = historyRows.map((h) => h.id);
        let refundMap: Record<string, number> = {};
        try {
          const { data: refunds, error: refundErr } = await supabase
            .from("credit_transactions")
            .select("related_history_id, amount")
            .eq("type", "refund")
            .in("related_history_id", historyIds);

          if (!refundErr && refunds) {
            for (const r of refunds) {
              if (r.related_history_id) {
                refundMap[r.related_history_id] = (refundMap[r.related_history_id] || 0) + r.amount;
              }
            }
          }
        } catch (refundFetchErr) {
          console.error("[HistoryPage] Failed to fetch refund data:", refundFetchErr);
        }

        const mapped = historyRows.map((row) => rowToHistoryItem(row, refundMap));
        setHistoryItems(mapped);
        writeHistoryCache(mapped);
        setTotalServerCount(data.total ?? data.history.length);
      } else {
        setHistoryItems([]);
        writeHistoryCache([]);
        setTotalServerCount(0);
      }
    } catch (err) {
      console.error("[HistoryPage] fetch exception:", err);
      setHistoryItems([]);
      setTotalServerCount(0);
    } finally {
      cachedHistoryRef.current = [];
      setDbLoading(false);
      setIsPolling(false);
    }
  }, [user, session]);

  // 자동 폴링: pending 항목이 있을 때만 동작
  const pendingCount = useMemo(
    () => historyItems.filter((i) => i.status === "pending").length,
    [historyItems]
  );

  const hasPending = pendingCount > 0;
  const prevHasPending = useRef(false);

  useEffect(() => {
    // pending 상태가 0 → 1 이상으로 바뀔 때만 폴링 시작
    // 1 이상 → 0 으로 바뀔 때만 폴링 중단
    // (중간에 pendingCount 값만 바뀌는 건 무시 — 타이머 리셋 안 함)
    if (hasPending === prevHasPending.current) return;
    prevHasPending.current = hasPending;

    if (!hasPending) {
      // pending이 0이 됐으면 폴링 중단
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      setPollCountdown(POLL_INTERVAL_MS / 1000);
      return;
    }

    // pending이 생겼으면 폴링 시작
    setPollCountdown(POLL_INTERVAL_MS / 1000);

    const countdownTimer = setInterval(() => {
      setPollCountdown((prev) => {
        if (prev <= 1) return POLL_INTERVAL_MS / 1000;
        return prev - 1;
      });
    }, 1000);

    pollTimerRef.current = setInterval(() => {
      setIsPolling(true);
      fetchHistory();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(countdownTimer);
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [hasPending, fetchHistory]);

  useEffect(() => {
    if (user && session) {
      // 24시간 지난 발송 내역 자동 정리 (백그라운드)
      supabase.rpc("cleanup_old_sends", { hours_to_keep: 24 }).then(({ data, error }) => {
        if (!error && data?.deleted > 0) {
          console.log(`[HistoryPage] ${data.deleted}건의 오래된 발송 내역 정리 완료`);
        }
      });
      fetchHistory();
    }
  }, [user, session, fetchHistory]);

  // ── 예약 발송 폴링 (scheduled → 발송 처리) ──
  const { triggerNow: triggerScheduledPoll } = useScheduledSendPoller({
    onProcessed: (count) => {
      console.log(`[HistoryPage] ${count}건 예약 발송 처리됨, 목록 새로고침`);
      fetchHistory();
    },
  });

  const handleRefresh = useCallback(() => {
    setIsPolling(true);
    triggerScheduledPoll(); // 예약 발송도 함께 체크
    fetchHistory();
  }, [fetchHistory, triggerScheduledPoll]);

  const filteredItems = useMemo(() => {
    let items = [...historyItems];

    if (filter.search) {
      const q = filter.search.toLowerCase();
      items = items.filter(
        (i) =>
          i.message.toLowerCase().includes(q) ||
          (i.subject?.toLowerCase().includes(q) ?? false) ||
          i.recipients.some(
            (r) => r.phone.includes(q) || (r.name?.toLowerCase().includes(q) ?? false)
          )
      );
    }

    if (filter.dateFrom) {
      items = items.filter((i) => i.sentAt.replace(/\./g, "-").slice(0, 10) >= filter.dateFrom);
    }
    if (filter.dateTo) {
      items = items.filter((i) => i.sentAt.replace(/\./g, "-").slice(0, 10) <= filter.dateTo);
    }

    if (filter.types.length > 0) {
      items = items.filter((i) => filter.types.includes(i.type));
    }

    if (filter.statuses.length > 0) {
      items = items.filter((i) => filter.statuses.includes(i.status));
    }

    items.sort((a, b) => {
      let valA: number | string = 0;
      let valB: number | string = 0;
      if (filter.sortBy === "sentAt") { valA = a.sentAt; valB = b.sentAt; }
      else if (filter.sortBy === "count") { valA = a.recipients.length > 0 ? a.recipients.length : (a.totalCount > 0 ? a.totalCount : a.successCount + a.failCount); valB = b.recipients.length > 0 ? b.recipients.length : (b.totalCount > 0 ? b.totalCount : b.successCount + b.failCount); }
      else if (filter.sortBy === "cost") { valA = a.cost; valB = b.cost; }
      if (valA < valB) return filter.sortDir === "desc" ? 1 : -1;
      if (valA > valB) return filter.sortDir === "desc" ? 1 : 1;
      return 0;
    });

    return items;
  }, [filter, historyItems]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedItems = filteredItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const totalSent = historyItems.reduce((s, i) => s + i.successCount, 0);
  const totalFail = historyItems.reduce((s, i) => s + i.failCount, 0);
  const totalCost = historyItems.reduce((s, i) => s + i.cost, 0);
  const totalRefund = historyItems.reduce((s, i) => s + (i.refundAmount || 0), 0);
  const netCost = totalCost - totalRefund;
  const successRate = totalSent + totalFail > 0 ? Math.round((totalSent / (totalSent + totalFail)) * 100) : 0;

  const handleCancel = async (item: HistoryItem) => {
    if (!user || cancellingIds.has(item.id)) return;

    const confirmed = window.confirm(
      `정말로 이 발송을 중지하시겠습니까?\n\n` +
      `대상: ${item.totalCount > 0 ? item.totalCount.toLocaleString() : (item.successCount + item.failCount).toLocaleString()}명\n` +
      `진행: 성공 ${item.successCount.toLocaleString()} / 실패 ${item.failCount.toLocaleString()}\n\n` +
      `중지하면 현재까지 처리된 결과로 상태가 "실패"로 변경됩니다.`
    );

    if (!confirmed) return;

    setCancellingIds((prev) => new Set(prev).add(item.id));

    try {
      const { data, error } = await supabase.functions.invoke("force-cancel-send", {
        body: { history_id: item.id, user_id: user.id },
      });

      if (error || data?.error) {
        alert(`중지 실패: ${error?.message || data?.error || "알 수 없는 오류"}`);
        return;
      }

      await fetchHistory();
    } catch (err: any) {
      alert(`중지 중 오류 발생: ${err.message || "알 수 없는 오류"}`);
    } finally {
      setCancellingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === historyItems.length && historyItems.length > 0) {
        return new Set();
      }
      return new Set(historyItems.map((item) => item.id));
    });
  }, [historyItems]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBatchDelete = useCallback(async () => {
    if (!user || selectedIds.size === 0 || batchDeleting) return;

    const confirmed = window.confirm(
      `정말로 선택한 ${selectedIds.size}건의 발송 내역을 삭제하시겠습니까?\n\n` +
      `삭제된 데이터는 복구할 수 없습니다.`
    );

    if (!confirmed) return;

    setBatchDeleting(true);

    try {
      const { data, error } = await supabase.rpc("delete_send_histories", {
        p_user_id: user.id,
        p_ids: Array.from(selectedIds),
      });

      if (error) {
        console.error("[HistoryPage] batch delete error:", error);
        alert(`삭제 중 오류가 발생했습니다: ${error.message}`);
        return;
      }

      const deletedCount = data?.deleted ?? 0;
      if (deletedCount > 0) {
        setSelectedIds(new Set());
        await fetchHistory();
      }
    } catch (err: any) {
      console.error("[HistoryPage] batch delete exception:", err);
      alert(`삭제 중 오류가 발생했습니다: ${err.message || "알 수 없는 오류"}`);
    } finally {
      setBatchDeleting(false);
    }
  }, [user, selectedIds, batchDeleting, fetchHistory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }
  if (!user) return null;

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen pb-20 md:pb-0`}>
        {/* Top bar */}
        <header className="h-14 md:h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-base md:text-lg"></i>
            </button>
            <div>
              <h1 className="text-white font-semibold text-sm md:text-base">발송 내역</h1>
              <p className="text-gray-500 text-[10px] md:text-xs hidden sm:block">전체 발송 기록 조회</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <button
              onClick={() => navigate("/dashboard/deletion-logs")}
              className="hidden sm:flex items-center gap-1 text-gray-500 hover:text-gray-300 text-[10px] md:text-xs font-medium cursor-pointer px-2 py-1 rounded-lg hover:bg-white/[0.04] transition-colors"
              title="삭제 로그"
            >
              <i className="ri-delete-bin-6-line text-xs"></i>
              <span>삭제 로그</span>
            </button>
            <button
              onClick={handleRefresh}
              className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center cursor-pointer transition-colors rounded-lg ${
                pendingCount > 0
                  ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                  : "text-gray-400 hover:text-white hover:bg-white/[0.06]"
              }`}
              title="새로고침"
            >
              <i className={`ri-refresh-line text-sm md:text-base ${(dbLoading || isPolling) ? "animate-spin" : ""}`}></i>
            </button>
            <button
              onClick={() => navigate("/dashboard/send")}
              className="flex items-center gap-1 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-send-plane-line text-xs md:text-sm"></i>
              <span className="hidden sm:inline">새 문자 발송</span>
              <span className="sm:hidden">발송</span>
            </button>
          </div>
        </header>

        {/* Pending items polling banner */}
        {pendingCount > 0 && (
          <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-amber-500/10 border border-amber-500/25 rounded-xl px-4 py-3">
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className="ri-loader-4-line animate-spin text-amber-400 text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-amber-300 text-xs font-semibold">
                {pendingCount}건 처리 중
              </p>
              <p className="text-amber-400/70 text-[11px] mt-0.5">
                {pollCountdown}초 후 자동 새로고침 · 너무 오래 지속되면 중지 버튼을 눌러주세요
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={dbLoading || isPolling}
              className="flex items-center gap-1 bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:text-amber-200 hover:bg-amber-500/30 px-3 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer whitespace-nowrap transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className={`ri-refresh-line text-xs ${(dbLoading || isPolling) ? "animate-spin" : ""}`}></i>
              지금 새로고침
            </button>
            <button
              onClick={() => {
                const pendingItems = historyItems.filter((i) => i.status === "pending");
                if (pendingItems.length === 0) return;
                const confirmed = window.confirm(
                  `정말로 모든 처리 중 발송을 중지하시겠습니까?\n\n` +
                  `대상: ${pendingItems.length}건\n\n` +
                  `중지하면 모든 처리 중 항목이 "실패"로 변경됩니다.`
                );
                if (!confirmed) return;
                pendingItems.forEach((item) => handleCancel(item));
              }}
              className="flex items-center gap-1 bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 hover:bg-red-500/30 px-3 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-stop-circle-line text-xs"></i>
              전체 중지
            </button>
          </div>
        )}

        <div className="p-4 md:p-6 space-y-4 md:space-y-5">
          {/* Summary stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
            {[
              { label: "총 발송 건수", value: (totalSent + totalFail).toLocaleString(), unit: "건", icon: "ri-send-plane-line", color: "from-violet-500/20 to-violet-600/10", iconColor: "text-violet-400" },
              { label: "성공", value: totalSent.toLocaleString(), unit: "건", icon: "ri-checkbox-circle-line", color: "from-emerald-500/20 to-emerald-600/10", iconColor: "text-emerald-400" },
              { label: "실패", value: totalFail.toLocaleString(), unit: "건", icon: "ri-close-circle-line", color: "from-red-500/20 to-red-600/10", iconColor: "text-red-400" },
              { label: "총 비용", value: `₩${netCost.toLocaleString()}`, unit: "", icon: "ri-coins-line", color: "from-amber-500/20 to-amber-600/10", iconColor: "text-amber-400", sub: totalRefund > 0 ? `환불 ₩${totalRefund.toLocaleString()}` : undefined, subColor: totalRefund > 0 ? "text-emerald-400" : undefined },
            ].map((stat) => (
              <div key={stat.label} className={`bg-gradient-to-br ${stat.color} border border-white/[0.08] rounded-xl md:rounded-2xl p-3 md:p-5`}>
                <div className="flex items-center justify-between mb-2 md:mb-3">
                  <p className="text-gray-400 text-[10px] md:text-xs font-medium">{stat.label}</p>
                  <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-lg bg-white/[0.06]">
                    <i className={`${stat.icon} ${stat.iconColor} text-xs md:text-base`}></i>
                  </div>
                </div>
                <p className="text-white text-lg md:text-xl font-bold">
                  {stat.value}
                  {stat.unit && <span className="text-gray-400 text-xs md:text-sm font-normal ml-0.5 md:ml-1">{stat.unit}</span>}
                </p>
                {stat.sub && (
                  <p className={`${stat.subColor ?? "text-gray-400"} text-[10px] md:text-xs mt-1 font-medium`}>{stat.sub}</p>
                )}
                {stat.label === "성공" && (
                  <p className="text-emerald-400 text-[10px] md:text-xs mt-1 font-medium">성공률 {successRate}%</p>
                )}
              </div>
            ))}
          </div>

          {/* Type breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4">
            {(["SMS", "LMS", "MMS"] as const).map((t) => {
              const typeItems = historyItems.filter((i) => i.type === t);
              const count = typeItems.reduce((s, i) => s + (i.recipients.length > 0 ? i.recipients.length : (i.totalCount > 0 ? i.totalCount : i.successCount + i.failCount)), 0);
              const cost = typeItems.reduce((s, i) => s + i.cost, 0);
              const cfg = TYPE_CONFIG[t];
              return (
                <div key={t} className="bg-white/[0.03] border border-white/[0.08] rounded-xl md:rounded-2xl p-3 md:p-4 flex items-center gap-3 md:gap-4">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg md:rounded-xl bg-white/[0.05] shrink-0">
                    <span className={`text-xs md:text-sm font-bold px-1 md:px-1.5 py-0.5 rounded-md ${cfg.className}`}>{t}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm md:text-base font-bold">{count.toLocaleString()}건</p>
                    <p className="text-gray-500 text-[10px] md:text-xs">₩{cost.toLocaleString()}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-gray-500 text-[10px] md:text-xs">{typeItems.length}회 발송</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <HistoryFilters
            filter={filter}
            onChange={setFilter}
            totalCount={historyItems.length}
            filteredCount={filteredItems.length}
          />

          {/* Loading state */}
          {dbLoading ? (
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl md:rounded-2xl py-12 md:py-16 flex flex-col items-center justify-center gap-2 md:gap-3">
              <div className="w-7 h-7 md:w-8 md:h-8 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin"></div>
              <p className="text-gray-500 text-xs md:text-sm">발송 내역 불러오는 중...</p>
            </div>
          ) : historyItems.length === 0 ? (
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl md:rounded-2xl py-16 md:py-20 text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-4 rounded-full bg-white/[0.04]">
                <i className="ri-send-plane-line text-gray-600 text-2xl md:text-3xl"></i>
              </div>
              <p className="text-gray-400 text-sm md:text-base font-medium mb-1">아직 발송 내역이 없어요</p>
              <p className="text-gray-600 text-xs md:text-sm mb-4 md:mb-5">첫 문자를 발송해보세요!</p>
              <button
                onClick={() => navigate("/dashboard/send")}
                className="inline-flex items-center gap-1.5 md:gap-2 bg-gradient-to-r from-violet-600 to-pink-600 text-white px-4 py-2 md:px-5 md:py-2.5 rounded-xl text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line"></i>
                문자 발송하기
              </button>
            </div>
          ) : (
            <HistoryTable
              items={pagedItems}
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              onSelect={setSelectedItem}
              onCancel={handleCancel}
              cancellingIds={cancellingIds}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              onClearSelection={handleClearSelection}
              onBatchDelete={handleBatchDelete}
            />
          )}
        </div>

        <MobileBottomNav />
      </main>

      <HistoryDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onDeleted={fetchHistory} />
    </div>
  );
}